import React, { Fragment, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'

import { FaIcon } from 'SharedComponents'

const InnerList = ({ rowExtraProps, rowComponent, categoryColor, columnChildren, items }) => {
  const renderRow = (item, index) => {
    const parityClass = index % 2 === 0 ? 'odd' : 'even'
    const allProps = {
      ...item,
      className: parityClass,
      ...rowExtraProps,
      rowIndex: index,
      categoryColor: categoryColor,
    }
    const RowComponent = rowComponent
    return (
      <RowComponent
        key={ index }
        { ...allProps }
        columnChildren={ columnChildren }
      />
    )
  }

  return _.isEmpty(items) ? (
    <Fragment>
      <tr >
        <td colSpan={ 100 } className='text-center empty-row no_data'>
          { window.I18n.t('customer_center_comp.components.sim_golf_labels.no_events_or_leagues') }
        </td>
      </tr>
    </Fragment>
  ) : (
    items.map((item, index) => (
      <Fragment>
        { renderRow(item, index) }
      </Fragment>
    ))
  )

}

InnerList.propTypes = {
  items: PropTypes.array.isRequired,
  rowExtraProps: PropTypes.object.isRequired,
  columnChildren: PropTypes.array.isRequired,
  categoryColor: PropTypes.array.isRequired,
  rowComponent: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.func.isRequired,
  ]),
}

const SimTable = ({ items, className, width, rowComponent, rowExtraProps, children, categoryColor, enableHorizontalScroll, showHeader, showFooter, footerComponent }) => {
  const [sorting, setSorting] = useState({ field: 'startDate', ascending: false })
  const [currentItems, setCurrentItems] = useState([])

  useEffect(() => {
    const itemsCopy = items
    if (itemsCopy.length > 0) {
      const sortedItems = itemsCopy.sort((a, b) => {
        const date1 = new Date(a['startDate'])
        const date2 = new Date(b['startDate'])
        return date2.toLocaleDateString() - date1.toLocaleDateString() ? 1 : -1
      })

      setCurrentItems(sortedItems)
    } else {
      setCurrentItems([])
    }
  }, [items])

  useEffect(() => {
    if (currentItems.length > 0) {
      const itemsCopy = currentItems
      const sortedItems = itemsCopy.sort((a, b) => {
        if (sorting.field === 'startDate'){
          const date1 = new Date(a[sorting.field])
          const date2 = new Date(b[sorting.field])
          return date2.toLocaleDateString() - date1.toLocaleDateString() ? 1 : -1
        } else if (sorting.field === 'name' || sorting.field === 'seasonName') {
          const sorted = a[sorting.field].localeCompare(b[sorting.field])
          const sortedDesc = b[sorting.field].localeCompare(a[sorting.field])
          return sorting.ascending ? sorted : sortedDesc
        }
      })

      setCurrentItems([...sortedItems])
    }
  }, [sorting])

  const applySorting = (key, ascending) => {
    setSorting({ field: key, ascending: ascending })
  }

  const renderHeaderContent = (data) => {
    if (data.children) {
      return data.children
    } else if (data.dataKey !== '#') {
      return _.capitalize(data.dataKey)
    } else {
      return ''
    }
  }

  const renderHeader = (data) => {
    return (
      <th
        className={ `glg-table-header ${className || ''}` }
        style={ { width: width } }
      >
        <div onClick={() => applySorting(data.dataKey, data.dataKey === sorting.field ? !sorting.ascending : sorting.ascending)}>
          { renderHeaderContent(data) }
          { ['name', 'seasonName', 'startDate'].includes(data.dataKey) &&
            <div className='table_sort_icon'>
              <FaIcon icon='sort' />
            </div>
          }
        </div>
      </th>
    )
  }

  let tableBody = (
    <tbody>
      <InnerList
        items={ currentItems }
        rowExtraProps={ rowExtraProps }
        rowComponent={ rowComponent }
        columnChildren={ children }
        key={ Math.random() }
        categoryColor={ categoryColor }
      />
    </tbody>
  )

  return (
    <div className={ `table_sim_golf ${ enableHorizontalScroll ? 'table-responsive' : ''} ${ items.length === 0 ? 'empty-table' : '' } ${className}` } >
      <div>
        <div>
          <table className='table'>
            {showHeader && (
              <thead className='hidden-sm hidden-xs'>
                <tr>
                  {React.Children.map(children, child => (
                    child && renderHeader(child.props)
                  ))}
                </tr>
              </thead>
            )}
            { tableBody }
            {showFooter && (
              <tfoot>
                { footerComponent }
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  )
}

SimTable.propTypes = {
  items: PropTypes.array.isRequired,
  showHeader: PropTypes.bool,
  showFooter: PropTypes.bool,
  rowExtraProps: PropTypes.object,
  enableHorizontalScroll: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  width: PropTypes.string,
  categoryColor: PropTypes.array.isRequired,
  rowComponent: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.func.isRequired,
  ]),
  footerComponent: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.func.isRequired,
  ]),
}

SimTable.defaultProps = {
  rowExtraProps: {},
  showHeader: true,
  showFooter: false,
  enableHorizontalScroll: true,
}

export default SimTable